/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Heading,
    SimpleGrid,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import ProductScroller from '../product-scroller'
import useEinstein from '../../hooks/use-einstein'
import useIntersectionObserver from '@salesforce/retail-react-app/app/hooks/use-intersection-observer'
import {useManualLinking} from '../../hooks/use-manual-linking'
import {useWishlistActions} from '@app/hooks/use-wishlist-actions'
import ProductTile from '../product-tile'
import {ProductCarousel} from '../product-carousel'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'

/**
 * RecommendedProducts is a React component that displays a list of recommended products
 * based on either a specified zone or recommender. It supports various display options
 * such as a carousel or a grid layout, and integrates with wishlist functionality.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.zone - The zone identifier for fetching recommendations.
 * @param {string} props.recommender - The recommender name for fetching recommendations.
 * @param {Array} props.products - An array of initial products to display.
 * @param {string} props.title - The title to display above the product recommendations.
 * @param {Function} props.shouldFetch - A function that determines if recommendations should be fetched.
 * @param {boolean} [props.isScroller=true] - Determines if the component should use a scroller layout.
 * @param {boolean|Object} [props.isCarousel=true] - Determines if the component should use a carousel layout.
 * @param {boolean} [props.isSwitchedImages=false] - Determines if images should be switched.
 * @param {Object} [props.nbSlidesToShow={md: 3, lg: 4}] - Number of slides to show in the carousel at different breakpoints.
 * @param {number} [props.maxProducts=4] - Maximum number of products to display.
 * @param {Object} props.titleProps - Additional properties for the title element.
 * @param {Object} props.props - Additional properties to pass to the component.
 *
 * @returns {JSX.Element|null} The rendered component or null if no recommendations are available.
 *
 * @example
 * <RecommendedProducts
 *   zone="homepage"
 *   recommender="bestsellers"
 *   products={initialProducts}
 *   title="Recommended for You"
 *   shouldFetch={() => true}
 *   isScroller={false}
 *   isCarousel={{ base: false, md: true }}
 *   isSwitchedImages={true}
 *   nbSlidesToShow={{ md: 2, lg: 3 }}
 *   maxProducts={5}
 *   titleProps={{ color: 'blue' }}
 * />
 */
const RecommendedProducts = ({
    zone,
    recommender,
    products,
    title,
    shouldFetch,
    isScroller = true,
    isCarousel = true,
    isSwitchedImages = false,
    nbSlidesToShow = {md: 3, lg: 4, xl: 4},
    maxProducts = 4,
    isCompleteTheLook = false,
    isAddToCartModal = false,
    titleProps,
    ...props
}) => {
    const {
        isLoading,
        recommendations,
        getZoneRecommendations,
        getRecommendations,
        sendClickReco,
        sendViewReco
    } = useEinstein()
    const currentProduct = isCompleteTheLook && products ? products[0] : {}

    const {manualLinkingResult, isManualLinkingEnabled} = useManualLinking(
        currentProduct,
        'c_shopTheLook'
    )

    const {wishlist, addProductToWishlist, removeProductFromWishlist} = useWishlistActions()
    const {triggerViewItemListEvent} = useGTM()

    const ref = useRef()
    const isOnScreen = useIntersectionObserver(ref, {useOnce: true})
    const [_products, setProducts] = useState(products)
    const isMobile = useBreakpointValue({base: true, md: false})
    const isCarouselFromBreakpoint = useBreakpointValue(
        typeof isCarousel === 'object' ? isCarousel : {base: isCarousel}
    )

    useEffect(() => {
        // Check if the component should fetch results or not. This is useful
        // when you are still waiting on additional data, like `products`.
        if (typeof shouldFetch === 'function' && !shouldFetch()) {
            return
        }

        // Fetch either zone or recommender, but not both. If a zone and recommender
        // name are both provided, `zone` takes precendence.
        if (zone) {
            getZoneRecommendations(zone, _products)
            return
        }
        if (recommender) {
            getRecommendations(recommender, _products)
            return
        }
    }, [zone, recommender, _products])

    useEffect(() => {
        // This is an optimization that eliminates superfluous rerenders/fetching by
        // keeping a copy of the `products` array prop in state for shallow comparison.
        if (!Array.isArray(products)) {
            return
        }
        if (
            products.length !== _products?.length ||
            !products.every((val, index) => val === _products?.[index])
        ) {
            setProducts(products)
        }
    }, [products])

    useEffect(() => {
        if (isOnScreen && recommendations?.recs) {
            sendViewReco(
                {
                    recommenderName: recommendations.recommenderName,
                    __recoUUID: recommendations.recoUUID
                },
                recommendations.recs.map((rec) => ({id: rec.id}))
            )
        }
    }, [isOnScreen, recommendations])

    // GTM Integration
    const [hasTriggeredGTM, setHasTriggeredGTM] = useState(false)
    useEffect(() => {
        if (!hasTriggeredGTM && recommendations && recommendations?.recs) {
            triggerViewItemListEvent(recommendations?.recs, 'alternative')
            setHasTriggeredGTM(true)
        }
    }, [recommendations])

    // The component should remove itself altogether if it has no recommendations
    // and we aren't loading any.
    if (
        !isAddToCartModal &&
        (!isManualLinkingEnabled || manualLinkingResult?.length < 1) &&
        !isLoading &&
        (!recommendations || recommendations.length < 1)
    ) {
        return null
    }

    /**
     * Sends analytics data for a clicked recommended product.
     *
     * This function is responsible for sending click analytics data for a recommended product.
     * It utilizes the `sendClickReco` function to log the click event with the recommender's
     * name and UUID, and then triggers a specific click event based on the type of recommender.
     *
     * @param {Object} product - The product object that was clicked.
     * @param {string} product.productUrl - The URL of the product page, which is updated to the current window location.
     *
     * The function determines the type of event to trigger based on the recommender type:
     * - If the recommender is `EINSTEIN_RECOMMENDERS.PDP_COMPLETE_SET`, it triggers a 'click_complete_your_look' event.
     * - Otherwise, it triggers a 'click_related_products' event.
     *
     * The function modifies the `product` object by setting its `productUrl` property to the current page URL.
     */
    const sendAnalytics = (product) => {
        sendClickReco(
            {
                recommenderName: recommendations.recommenderName,
                __recoUUID: recommendations.recoUUID
            },
            product
        )
    }

    const productTileProps = (product) => ({
        onClick: () => {
            sendAnalytics(product)
        },
        isFavourite: wishlist?.customerProductListItems?.some(
            (item) => item.productId === product?.productId || product?.id
        ),
        onFavouriteToggle: (toBeFavourite) => {
            const action = toBeFavourite ? addProductToWishlist : removeProductFromWishlist
            return action(product)
        }
    })

    let productList = isManualLinkingEnabled ? manualLinkingResult : recommendations?.recs

    if (isAddToCartModal) {
        productList = products
    }

    if (!productList || productList?.length < 1) {
        return <></>
    }

    return !isScroller ? (
        <>
            {/* If not a carousel on mobile, render a grid of 2 columns of products */}
            {isMobile && (typeof isCarousel === 'object' ? !isCarousel?.base : !isCarousel) ? (
                <Box {...props}>
                    {title &&
                    (typeof title === 'string' ||
                        title?.type?.displayName === 'MemoizedFormattedMessage') ? (
                        <Heading
                            as="h2"
                            fontSize="4xl"
                            pb={[40, null, null, 34]}
                            pl={'10px'}
                            textTransform={'uppercase'}
                            {...titleProps}
                        >
                            {title}
                        </Heading>
                    ) : (
                        title
                    )}
                    <SimpleGrid columns={2} spacing={10} px={10} py={1} as="ul">
                        {productList?.slice(0, maxProducts).map((product, idx) => (
                            <Box key={product?.id || idx} width="100%" as="li" listStyleType="none">
                                <ProductTile
                                    data-testid="product-scroller-item"
                                    product={product}
                                    dynamicImageProps={{
                                        widths: ['50vw'],
                                        heights: ['auto', 'auto', 'auto', 'auto']
                                    }}
                                    sx={{
                                        width: '100%',
                                        height: 'auto'
                                    }}
                                    isSwitchedImages={isSwitchedImages}
                                    recommender={recommender ? recommender : null}
                                    {...productTileProps(product)}
                                />
                            </Box>
                        ))}
                    </SimpleGrid>
                </Box>
            ) : (
                <ProductCarousel
                    title={
                        <Heading as="h2" fontSize="4xl" textTransform={'uppercase'}>
                            {title}
                        </Heading>
                    }
                    nbSlidesToShow={nbSlidesToShow}
                    products={productList}
                    isLoading={isLoading}
                    isDisabled={!isCarouselFromBreakpoint}
                    isSwitchedImages={isSwitchedImages}
                    headerProps={titleProps}
                    isCompleteTheLook={isCompleteTheLook}
                    recommender={recommender ? recommender : null}
                    onClick={(product) => {
                        if (product?.id) {
                            sendAnalytics(product)
                        }
                    }}
                    {...props}
                />
            )}
        </>
    ) : (
        <ProductScroller
            ref={ref}
            title={title || recommendations?.displayMessage}
            products={productList}
            isLoading={isLoading}
            productTileProps={productTileProps}
            recommender={recommender ? recommender : null}
            {...props}
        />
    )
}

RecommendedProducts.propTypes = {
    /* The title to appear above the product scroller */
    title: PropTypes.any,

    /* The zone to request */
    zone: PropTypes.string,

    /* The recommender to request */
    recommender: PropTypes.string,

    /* The products to use for recommendation context */
    products: PropTypes.arrayOf(PropTypes.object),

    /* Callback to determine if the component should fetch results */
    shouldFetch: PropTypes.func,

    /* Callback to determine if the component should be a scroller */
    isScroller: PropTypes.bool,

    /* Callback to determine if the component should be a carousel on each breakpoint */
    isCarousel: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),

    /* Callback to determine if the component should switch images in carousel */
    isSwitchedImages: PropTypes.bool,

    /* Allow to adjust number of slides to show */
    nbSlidesToShow: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),

    /* Props to pass to the title component */
    titleProps: PropTypes.object,

    /* Allow to adjust number of products for related products */
    maxProducts: PropTypes.number,

    /* Determine if the recommender is overridable */
    isCompleteTheLook: PropTypes.bool,

    /* Determine if the context is the add to cart modal*/
    isAddToCartModal: PropTypes.bool
}

export default RecommendedProducts
